import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthenticationCard } from '@components/atoms/AuthenticationCard';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Space } from '@components/atoms/Space';
import { Form } from '@components/atoms/Form';
import { REGEX_IS_EMAIL } from '@constants';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { useNotifications } from '@hooks/useNotifications';
import { useRequestResetPassword } from '@services/useRequestResetPassword';
import { useLocation } from 'react-router';

export const PasswordRecoveryCard: FC = () => {
    const { t } = useTranslation();

    const translations = {
        title: t('password-recovery-card-component.title'),
        subtitle: t('password-recovery-card-component.subtitle'),
        noAccount: t('password-recovery-card-component.no-account'),
        createAccountButton: t('password-recovery-card-component.create-account.button'),
    };

    return (
        <AuthenticationCard title={translations.title} subtitle={translations.subtitle}>
            <>
                <PasswordRecoveryForm />
                <Space spaceBreakpoints={{ xs: { space: 30 } }} />

                {/* create account */}
                <AuthenticationCard.Divider text={translations.noAccount} />
                <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                <Anchor href="register" mode={AnchorMode.primary}>
                    {translations.createAccountButton}
                </Anchor>
            </>
        </AuthenticationCard>
    );
};

interface PasswordRecoveryFormErrors {
    email: string[];
}

const PasswordRecoveryForm: FC = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const getParamEmail = params.get('email');
    const { showDefaultNotification } = useNotifications();
    const { request: requestResetPassword, loading, error: errorResetPassword, lastResponseTime } = useRequestResetPassword();
    const { t } = useTranslation();

    const formRef = useRef<HTMLFormElement>(null);
    const [email, setEmail] = useState<string>(getParamEmail || '');

    const translations = {
        emailLabel: t('password-recovery-card-component.email.label'),
        resetPasswordButton: t('password-recovery-card-component.reset-password.button'),
        emailRequiredError: t('password-recovery-card-component.email.required-error'),
        emailInvalidError: t('password-recovery-card-component.email.invalid-error'),
        feedback: t('password-recovery-card-component.feedback', { email: email }),
    };

    // errors to be displayed in the form
    const [errors, setErrors] = useState<PasswordRecoveryFormErrors>({
        email: [],
    });

    const handleOnClickRecoverPassword = () => {
        const errors = validateForm();

        if (errors.email.length === 0) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => PasswordRecoveryFormErrors = () => {
        const newErrors: PasswordRecoveryFormErrors = {
            email: [],
        };

        // check if email is inserted
        if (!email.trim()) {
            newErrors.email.push(translations.emailRequiredError);
        } else {
            // check if email is valid
            !REGEX_IS_EMAIL.test(email.trim()) && newErrors.email.push(translations.emailInvalidError);
        }

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm: () => void = () => {
        requestResetPassword({ email: email });
    };

    // if logged in successfully, go to homepage
    // if not, show error
    useEffect(() => {
        if (lastResponseTime) {
            if (errorResetPassword) {
                setErrors({ ...errors, email: [errorResetPassword] });
            } else {
                // dispatch notification
                showDefaultNotification({ type: 'success', message: translations.feedback });
                // reset form fields
                setEmail('');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    return (
        <Form
            formRef={formRef}
            classes="d-flex flex-column"
            onEnterPress={handleOnClickRecoverPassword}
            onSubmit={() => submitForm()}>
            <>
                {/* email */}
                <Input
                    type={InputTypes.email}
                    label={`${translations.emailLabel}*`}
                    variant={InputVariants.labeledInput}
                    value={email}
                    onChange={(value) => setEmail(value)}
                    state={errors.email.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.email}
                />

                <Space spaceBreakpoints={{ xs: { space: 15 }, md: { space: 30 } }} />

                {/* reset password */}
                <Button
                    label={translations.resetPasswordButton}
                    classes="d-block"
                    mode={ButtonMode.primary}
                    onClick={() => handleOnClickRecoverPassword()}
                    disabled={loading}
                />
            </>
        </Form>
    );
};
